import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Bell, Loader2, Settings, Clock, AlertTriangle, CheckCircle, Save, RefreshCw, DollarSign, Building, Calendar, Mail, Plus, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const formatCurrency = (val) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(val || 0);

export default function AlertasPagosPage() {
  const [config, setConfig] = useState({ dias_alerta_1: 7, dias_alerta_2: 3, dias_alerta_3: 1, enviar_email: false, destinatarios_email: [], activo: true });
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("alertas");
  const [nuevoEmail, setNuevoEmail] = useState("");
  
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, alertasRes] = await Promise.all([
        axios.get(`${API}/cuentas-pagar/alertas/config`, getAuthHeaders()),
        axios.get(`${API}/cuentas-pagar/alertas/pendientes`, getAuthHeaders())
      ]);
      setConfig(configRes.data);
      setAlertas(alertasRes.data.alertas || []);
    } catch (err) { toast.error("Error al cargar datos"); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cuentas-pagar/alertas/config`, config, getAuthHeaders());
      toast.success("Configuración guardada");
    } catch (err) { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case "critica": return "bg-red-100 text-red-700 border-red-200";
      case "alta": return "bg-orange-100 text-orange-700 border-orange-200";
      case "media": return "bg-yellow-100 text-yellow-700 border-yellow-200";
      default: return "bg-blue-100 text-blue-700 border-blue-200";
    }
  };

  const getAlertIcon = (tipo) => {
    switch (tipo) {
      case "vencida": return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case "urgente": return <Clock className="w-5 h-5 text-orange-600" />;
      case "proxima": return <Calendar className="w-5 h-5 text-yellow-600" />;
      default: return <Bell className="w-5 h-5 text-blue-600" />;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 overflow-auto">
        <div className="max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Bell className="w-7 h-7 text-[#0C314C]" />Alertas de Pagos
              </h1>
              <p className="text-slate-500 mt-1">Monitorea facturas próximas a vencer y configura alertas</p>
            </div>
            <Button onClick={fetchData} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Actualizar
            </Button>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="alertas" className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />Alertas Activas ({alertas.length})
              </TabsTrigger>
              <TabsTrigger value="configuracion" className="flex items-center gap-2">
                <Settings className="w-4 h-4" />Configuración
              </TabsTrigger>
            </TabsList>

            {/* Alertas Tab */}
            <TabsContent value="alertas">
              {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" /></div>
              ) : alertas.length === 0 ? (
                <Card>
                  <CardContent className="py-16 text-center">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">¡Todo al día!</h3>
                    <p className="text-slate-500">No hay facturas próximas a vencer</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Card className="border-red-200 bg-red-50">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                          <AlertTriangle className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-red-700">{alertas.filter(a => a.tipo === "vencida").length}</p>
                          <p className="text-sm text-red-600">Vencidas</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-orange-200 bg-orange-50">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                          <Clock className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-orange-700">{alertas.filter(a => a.tipo === "urgente").length}</p>
                          <p className="text-sm text-orange-600">Urgentes (1-3 días)</p>
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="border-yellow-200 bg-yellow-50">
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                          <Calendar className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-yellow-700">{alertas.filter(a => a.tipo === "proxima" || a.tipo === "recordatorio").length}</p>
                          <p className="text-sm text-yellow-600">Próximas (4-7 días)</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Alerts List */}
                  <ScrollArea className="h-[calc(100vh-400px)] min-h-[300px]">
                    <div className="space-y-3 pr-4">
                      {alertas.map((alerta) => (
                        <Card key={alerta.id} className={`border-l-4 ${alerta.tipo === "vencida" ? "border-l-red-500" : alerta.tipo === "urgente" ? "border-l-orange-500" : "border-l-yellow-500"} hover:shadow-md transition-shadow cursor-pointer`}
                          onClick={() => navigate("/cuentas-pagar")}>
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${alerta.tipo === "vencida" ? "bg-red-100" : alerta.tipo === "urgente" ? "bg-orange-100" : "bg-yellow-100"}`}>
                                {getAlertIcon(alerta.tipo)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-start justify-between mb-2">
                                  <div>
                                    <h4 className="font-semibold text-slate-800">{alerta.proveedor_nombre}</h4>
                                    <p className="text-sm text-slate-600">Factura #{alerta.numero_factura}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Badge className={getPriorityColor(alerta.prioridad)}>
                                      {alerta.prioridad === "critica" ? "Crítica" : alerta.prioridad === "alta" ? "Alta" : alerta.prioridad === "media" ? "Media" : "Baja"}
                                    </Badge>
                                  </div>
                                </div>
                                <p className="text-sm text-slate-700 font-medium mb-2">{alerta.titulo}</p>
                                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500">
                                  <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />{formatCurrency(alerta.saldo_pendiente)}</span>
                                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />Vence: {alerta.fecha_vencimiento}</span>
                                  <Badge variant="outline" className="text-xs">{alerta.categoria}</Badge>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}
            </TabsContent>

            {/* Configuración Tab */}
            <TabsContent value="configuracion">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5" />Configuración de Alertas</CardTitle>
                  <CardDescription>Define los días de anticipación para recibir alertas de vencimiento</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-base font-medium">Sistema de Alertas Activo</Label>
                      <p className="text-sm text-slate-500">Habilita o deshabilita todas las alertas de pagos</p>
                    </div>
                    <Switch checked={config.activo} onCheckedChange={(checked) => setConfig({...config, activo: checked})} disabled={!isAdmin} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Bell className="w-4 h-4 text-blue-600" />Primera Alerta (días antes)</Label>
                      <Input type="number" min="1" max="30" value={config.dias_alerta_1} onChange={(e) => setConfig({...config, dias_alerta_1: parseInt(e.target.value) || 1})} disabled={!isAdmin} />
                      <p className="text-xs text-slate-500">Notificación de recordatorio temprano</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><Clock className="w-4 h-4 text-yellow-600" />Segunda Alerta (días antes)</Label>
                      <Input type="number" min="1" max="30" value={config.dias_alerta_2} onChange={(e) => setConfig({...config, dias_alerta_2: parseInt(e.target.value) || 1})} disabled={!isAdmin} />
                      <p className="text-xs text-slate-500">Alerta de prioridad media</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-600" />Tercera Alerta (días antes)</Label>
                      <Input type="number" min="1" max="30" value={config.dias_alerta_3} onChange={(e) => setConfig({...config, dias_alerta_3: parseInt(e.target.value) || 1})} disabled={!isAdmin} />
                      <p className="text-xs text-slate-500">Alerta urgente de último momento</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label className="text-base font-medium">Enviar Alertas por Email</Label>
                      <p className="text-sm text-slate-500">Recibe notificaciones por correo electrónico</p>
                    </div>
                    <Switch checked={config.enviar_email} onCheckedChange={(checked) => setConfig({...config, enviar_email: checked})} disabled={!isAdmin} />
                  </div>

                  {/* Campo de destinatarios de email - solo visible cuando enviar_email está activo */}
                  {config.enviar_email && (
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                      <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-blue-600" />
                        <Label className="text-base font-medium text-blue-800">Destinatarios de Alertas</Label>
                      </div>
                      <p className="text-sm text-blue-600">Agrega los correos electrónicos que recibirán las alertas de vencimiento</p>
                      
                      {/* Input para agregar nuevo email */}
                      <div className="flex gap-2">
                        <Input 
                          type="email" 
                          placeholder="ejemplo@correo.com" 
                          value={nuevoEmail}
                          onChange={(e) => setNuevoEmail(e.target.value)}
                          className="flex-1"
                          disabled={!isAdmin}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && nuevoEmail.trim()) {
                              e.preventDefault();
                              if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nuevoEmail.trim())) {
                                if (!config.destinatarios_email.includes(nuevoEmail.trim())) {
                                  setConfig({...config, destinatarios_email: [...config.destinatarios_email, nuevoEmail.trim()]});
                                  setNuevoEmail("");
                                } else {
                                  toast.error("Este email ya está en la lista");
                                }
                              } else {
                                toast.error("Por favor ingresa un email válido");
                              }
                            }
                          }}
                        />
                        <Button 
                          type="button"
                          variant="outline"
                          className="border-blue-300 text-blue-700 hover:bg-blue-100"
                          disabled={!isAdmin || !nuevoEmail.trim()}
                          onClick={() => {
                            if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nuevoEmail.trim())) {
                              if (!config.destinatarios_email.includes(nuevoEmail.trim())) {
                                setConfig({...config, destinatarios_email: [...config.destinatarios_email, nuevoEmail.trim()]});
                                setNuevoEmail("");
                              } else {
                                toast.error("Este email ya está en la lista");
                              }
                            } else {
                              toast.error("Por favor ingresa un email válido");
                            }
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />Agregar
                        </Button>
                      </div>

                      {/* Lista de emails agregados */}
                      {config.destinatarios_email.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {config.destinatarios_email.map((email, index) => (
                            <Badge 
                              key={index} 
                              className="bg-white border border-blue-300 text-blue-700 px-3 py-1.5 flex items-center gap-2 hover:bg-blue-100"
                            >
                              <Mail className="w-3 h-3" />
                              {email}
                              {isAdmin && (
                                <button 
                                  onClick={() => setConfig({
                                    ...config, 
                                    destinatarios_email: config.destinatarios_email.filter((_, i) => i !== index)
                                  })}
                                  className="ml-1 hover:text-red-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-blue-500 italic">No hay destinatarios agregados. Agrega al menos un email para recibir alertas.</p>
                      )}
                    </div>
                  )}

                  {isAdmin && (
                    <div className="flex justify-end pt-4 border-t">
                      <Button onClick={handleSaveConfig} disabled={saving} className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white">
                        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Guardar Configuración
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
